import { ApiError, notifyPanelistNotFound } from "./errors";
import { handleMockRequest } from "./mockServer";
import { getFlexSessionId } from "./session";

interface ApiRequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  noAuth?: boolean;
  isRetry?: boolean;
  params?: Record<string, any>;
}

type AuthTokenGetter = (skipCache?: boolean) => Promise<string | null>;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;
const USE_MOCK_API =
  !API_BASE_URL || import.meta.env.VITE_USE_MOCK_API === "true";
let authTokenGetter: AuthTokenGetter | null = null;
let apiRequestsBlocked = false;
let sessionReinitializationPromise: Promise<unknown> | null = null;
let unauthorizedSessionHandler: (() => void | Promise<void>) | null = null;

export function setAuthTokenGetter(getter: AuthTokenGetter | null) {
  authTokenGetter = getter;
}

export function getAuthTokenGetter() {
  return authTokenGetter;
}

export function setApiRequestsBlocked(blocked: boolean) {
  apiRequestsBlocked = blocked;
}

export function setUnauthorizedSessionHandler(
  handler: (() => void | Promise<void>) | null,
) {
  unauthorizedSessionHandler = handler;
}

export function getApiBaseUrl() {
  if (!API_BASE_URL) {
    throw new ApiError("API base URL is not configured.", 500);
  }

  return API_BASE_URL;
}

export async function getAuthToken(
  skipCache?: boolean,
): Promise<string | null> {
  return authTokenGetter ? await authTokenGetter(skipCache) : null;
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  if (apiRequestsBlocked) {
    throw new ApiError("API requests are temporarily blocked.", 401);
  }

  const method = options.method ?? "GET";
  const headers = new Headers(options.headers);
  let token = await getAuthToken();
  if (!token) {
    token = options.noAuth ? null : await getAuthToken(true);
  }
  const flexSessionId = getFlexSessionId();

  headers.set("Accept", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (flexSessionId) headers.set("X-Flex-Session-Id", flexSessionId);

  const requestBody =
    options.body === undefined ? undefined : JSON.stringify(options.body);
  if (requestBody && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (USE_MOCK_API) {
    return handleMockRequest<T>({
      body: options.body,
      headers,
      method,
      path,
    });
  }

  const url = new URL(`${API_BASE_URL}${path}`);

  if (options.params) {
    Object.entries(options.params).forEach(([key, value]) => {
      if (value === undefined) return;
      if (typeof value === "object")
        return url.searchParams.set(key, JSON.stringify(value));
      url.searchParams.set(key, value);
    });
  }

  const response = await fetch(url, {
    ...options,
    body: requestBody,
    credentials: "include",
    headers,
    method,
  });

  if (
    response.status === 401 &&
    !options.isRetry &&
    path !== "/session/context"
  ) {
    await reinitializeSession();
    return apiRequest<T>(path, {
      ...options,
      isRetry: true,
    });
  }

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const errorBody = (await response.json()) as { message?: string };
      message = errorBody.message || message;
    } catch {
      // The fallback status message is enough when the response is not JSON.
    }
    const error = new ApiError(message, response.status);
    notifyPanelistNotFound(error);
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export async function apiBlobRequest(
  path: string,
  options: ApiRequestOptions = {},
): Promise<Blob> {
  if (apiRequestsBlocked) {
    throw new ApiError("API requests are temporarily blocked.", 401);
  }

  if (USE_MOCK_API) {
    throw new ApiError(
      "File requests are unavailable when the mock API is enabled.",
      501,
    );
  }

  const method = options.method ?? "GET";
  const headers = new Headers(options.headers);
  let token = await getAuthToken();
  if (!token) {
    token = options.noAuth ? null : await getAuthToken(true);
  }
  const flexSessionId = getFlexSessionId();

  headers.set("Accept", "*/*");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (flexSessionId) headers.set("X-Flex-Session-Id", flexSessionId);

  const requestBody =
    options.body === undefined ? undefined : JSON.stringify(options.body);
  if (requestBody && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const url = new URL(`${API_BASE_URL}${path}`);

  if (options.params) {
    Object.entries(options.params).forEach(([key, value]) => {
      if (value === undefined) return;
      if (typeof value === "object")
        return url.searchParams.set(key, JSON.stringify(value));
      url.searchParams.set(key, value);
    });
  }

  const response = await fetch(url, {
    ...options,
    body: requestBody,
    credentials: "include",
    headers,
    method,
  });

  if (
    response.status === 401 &&
    !options.isRetry &&
    path !== "/session/context"
  ) {
    await reinitializeSession();
    return apiBlobRequest(path, {
      ...options,
      isRetry: true,
    });
  }

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const contentType = response.headers.get("content-type") ?? "";
      if (contentType.includes("application/json")) {
        const errorBody = (await response.json()) as { message?: string };
        message = errorBody.message || message;
      } else {
        const errorText = await response.text();
        if (errorText) {
          message = errorText;
        }
      }
    } catch {
      // The fallback status message is enough when the response body is not readable.
    }
    const error = new ApiError(message, response.status);
    notifyPanelistNotFound(error);
    throw error;
  }

  if (response.status === 204) {
    return new Blob();
  }

  return response.blob();
}

export async function reinitializeSession() {
  if (!sessionReinitializationPromise) {
    sessionReinitializationPromise = apiRequest("/session/context", {
      body: { fingerprint: getClientFingerprint() },
      isRetry: true,
      method: "POST",
    })
      .catch(async (error) => {
        if (
          error instanceof ApiError &&
          (error.status === 401 || error.status === 403)
        ) {
          await unauthorizedSessionHandler?.();
        }
        throw error;
      })
      .finally(() => {
        sessionReinitializationPromise = null;
      });
  }

  await sessionReinitializationPromise;
}

function getClientFingerprint() {
  return {
    language: navigator.language,
    screenHeight: window.screen.height,
    screenWidth: window.screen.width,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}
